import React, { useCallback } from 'react';
import { useToggle } from '../../../../hooks/useToggle';
import { RenderOptionsArgs, SelectBaseProps } from './types';
import InputBase from '../InputBase';
import { LayoutChangeEvent, View } from 'react-native';
import SelectChevron from './SelectChevron';
import { useIsDisabledByParent } from '../../IsDisabledProvider';

export type SelectBaseInputOwnProps = {
    renderOptions: (args: RenderOptionsArgs) => React.ReactNode;
    onLayout?: (event: LayoutChangeEvent) => void;
    inputRef?: any;
};

export type SelectBaseInputProps = SelectBaseInputOwnProps &
    Omit<SelectBaseProps, 'renderDesktopOptions' | 'renderMobileOptions'>;

const Options: React.FC<
    Pick<SelectBaseInputProps, 'renderOptions'> & {
        hasFocus: boolean;
        onCloseSelect: () => void;
        onOpenSelect: () => void;
        onSelect: (value: string) => void;
        onFocus: () => void;
        onBlur: () => void;
    }
> = (props) => {
    const onSelect = useCallback(
        (value: any) => {
            props.onSelect(value);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.onSelect, props.onCloseSelect, props.onBlur],
    );
    const onCloseSelect = useCallback(() => {
        props.onCloseSelect();
        props.onBlur();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onCloseSelect, props.onBlur]);
    const onOpenSelect = useCallback(() => {
        props.onOpenSelect();
        props.onFocus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onOpenSelect, props.onFocus]);

    return props.renderOptions({
        hasFocus: props.hasFocus,
        onCloseSelect,
        onOpenSelect,
        onSelect,
    });
};

const SelectBaseInput: React.FC<SelectBaseInputProps> = ({
    renderOptions,
    renderInputValue,
    isDisabled,
    inputValue,
    staticInputDisplay,
    inputRef,
    ...toPass
}) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    const selectOpenToggle = useToggle(false);

    return (
        <InputBase
            {...toPass}
            inputRef={inputRef}
            value={inputValue}
            isDisabled={isDisabledByParent}
            onInputPress={selectOpenToggle.toggleOn}
            disableRootPressableOnBlur
            staticInputDisplay={staticInputDisplay}
            renderInputField={({ onFocus, onBlur, hasFocus }) => (
                <>
                    <View
                        style={{ width: '100%', flexDirection: 'row', alignItems: 'flex-end' }}
                        testID={toPass.testID}
                    >
                        {renderInputValue()}
                        <SelectChevron isFocused={hasFocus} />
                    </View>
                    <Options
                        {...toPass}
                        hasFocus={hasFocus}
                        onFocus={onFocus as any}
                        onBlur={onBlur as any}
                        onOpenSelect={selectOpenToggle.toggleOn}
                        onCloseSelect={selectOpenToggle.toggleOff}
                        onSelect={toPass.onSelect}
                        renderOptions={renderOptions}
                    />
                </>
            )}
        />
    );
};

export default React.memo(SelectBaseInput);
